import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAdminInviteMember, useAdminListMembers } from '@/hooks';
import { useMemberStore } from '@/store/memberStore';
import { formatDate } from "@/utils/dateUtils";
import { Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { HubAdminMemberActions } from './HubAdminMemberActions';
import { Notice } from '@/components/ui/notice';

// Helper function to ensure selectedMemberIds is always an array
const ensureArray = (value: any): number[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
};

export function HubAdminMember() {
  // Backend operations - React Query
  const { data: membersData, isLoading, refetch: refetchMembers } = useAdminListMembers();
  const members = membersData?.members || [];
  const fetchMembers = () => refetchMembers();
  const inviteMemberMutation = useAdminInviteMember();
  
  // UI state - Zustand
  const {
    selectedMemberIds,
    toggleMemberSelection
  } = useMemberStore();
  
  // Ensure selectedMemberIds is an array
  const safeSelectedMemberIds = ensureArray(selectedMemberIds);
  
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');
  const [adminErrorMessage, setAdminErrorMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // Fetch current members on component mount.
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setInviteErrorMessage('');
    setIsInviting(true);
    
    try {
      await inviteMemberMutation.mutateAsync({
        email,
        firstName,
        surname
      });
      
      setSuccessMessage('Invitation sent successfully.');
      setEmail('');
      setFirstName('');
      setSurname('');
      // Refresh the member list immediately on success
      await fetchMembers();
    } catch (error) {
      // Error handling is done in the API hook.
      // If the error indicates a duplicate invite, clear the form and show error message.
      if (error instanceof Error && error.message.includes('has already been invited')) {
        setInviteErrorMessage(error.message);
        setEmail('');
        setFirstName('');
        setSurname('');
      }
    } finally {
      setIsInviting(false);
    }
  };

  // Split members into admin and non-admin lists, sorted by creation date (ascending)
  const adminMembers = members?.filter(emp => emp.isAdmin)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) || [];
    
  const nonAdminMembers = members?.filter(emp => !emp.isAdmin)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) || [];

  const renderMemberList = (memberList: typeof members, title: string) => (
    <div className="mt-0">
      <h4 className="text-muted-foreground font-medium border-b py-2 italic">{title}</h4>
      {memberList.length === 0 ? (
        <p className="text-muted-foreground border-b py-2">No {title.toLowerCase()} found.</p>
      ) : (
        <ul>
          {memberList.map((emp) => (
            <li key={emp.id} className="border-b py-2">
              {/* Mobile Layout (<=md) */}
              <div className="flex md:hidden">
                {/* Checkbox - vertically centered */}
                <div className="flex items-center mr-3">
                  <Checkbox
                    id={`member-${emp.id}`}
                    checked={safeSelectedMemberIds.includes(emp.id)}
                    onCheckedChange={() => toggleMemberSelection(emp.id)}
                  />
                </div>
                
                {/* Details in separate rows */}
                <div className="flex-1 space-y-1">
                  {/* Row 1: Name */}
                  <div className="text-sm font-medium">
                    {emp.firstName} {emp.surname} ({emp.email})
                  </div>
                  
                  {/* Row 2: ID */}
                  {emp.id && (
                    <div className="text-xs text-muted-foreground">
                      <span className="bg-muted text-muted-foreground px-1.5 py-0.5 rounded">ID: {emp.id}</span>
                    </div>
                  )}
                  
                  {/* Row 3: Tags */}
                  <TooltipProvider>
                    <div className="flex flex-wrap gap-1">
                      <Tooltip>
                        <TooltipTrigger>
                          <span
                            className={`text-xs px-2 py-1 rounded-full text-center ${emp.isAdmin ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                          >
                            {emp.isAdmin ? 'Admin' : 'User'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {emp.isAdmin ? 'User has administrator privileges' : 'Regular user without administrator privileges'}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger>
                          <span
                            className={`text-xs px-2 py-1 rounded-full text-center ${emp.accountStatus === 'VERIFIED' ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                          >
                            {emp.accountStatus === 'VERIFIED' ? 'Verified' : 'Unverified'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {emp.accountStatus === 'VERIFIED' ? 'User has verified their account' : 'User has not yet verified their account'}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger>
                          <span
                            className={`text-xs px-2 py-1 rounded-full text-center ${emp.isActive ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                          >
                            {emp.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {emp.isActive ? 'User account is active' : 'User account is inactive'}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                  
                  {/* Row 4: Datetime */}
                  <div className="text-xs text-muted-foreground">
                    {formatDate(new Date(emp.createdAt))}
                  </div>
                </div>
              </div>
              
              {/* Desktop Layout (md+) - Original */}
              <div className="hidden md:flex md:items-center md:justify-between">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id={`member-${emp.id}-desktop`}
                    checked={safeSelectedMemberIds.includes(emp.id)}
                    onCheckedChange={() => toggleMemberSelection(emp.id)}
                  />
                  <label
                    htmlFor={`member-${emp.id}-desktop`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {emp.firstName} {emp.surname} ({emp.email})
                    {emp.id && <span className="ml-2 text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">ID: {emp.id}</span>}
                  </label>
                </div>
                <TooltipProvider>
                  <div className="flex flex-row items-center justify-end gap-4">
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          className={`text-xs px-2 py-1 rounded-full min-w-[70px] text-center ${emp.isAdmin ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                        >
                          {emp.isAdmin ? 'Admin' : 'User'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {emp.isAdmin ? 'User has administrator privileges' : 'Regular user without administrator privileges'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          className={`text-xs px-2 py-1 rounded-full min-w-[70px] text-center ${emp.accountStatus === 'VERIFIED' ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                        >
                          {emp.accountStatus === 'VERIFIED' ? 'Verified' : 'Unverified'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {emp.accountStatus === 'VERIFIED' ? 'User has verified their account' : 'User has not yet verified their account'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          className={`text-xs px-2 py-1 rounded-full min-w-[70px] text-center ${emp.isActive ? 'bg-muted text-muted-foreground' : 'bg-muted/50 text-muted-foreground'}`}
                        >
                          {emp.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {emp.isActive ? 'User account is active' : 'User account is inactive'}
                      </TooltipContent>
                    </Tooltip>
                    <time className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(new Date(emp.createdAt))}
                    </time>
                  </div>
                </TooltipProvider>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Invite New Member</h3>
        <form onSubmit={handleInvite} className="space-y-6 md:space-y-4 pb-4">
          <div className="flex flex-col md:flex-row">
            <div className="flex-1 flex flex-col md:flex-row md:items-center">
              <Input 
                type="email"
                className="flex-1 rounded-none border-x-0 border-t-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:rounded-l-md md:border-l md:border-t md:border-b md:max-w-[300px]"
                placeholder="Email Address ..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input 
                type="text"
                className="flex-1 mt-4 md:mt-0 rounded-none border-x-0 border-t-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:border-t md:border-l md:max-w-[300px]"
                placeholder="First Name ..."
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input 
                type="text"
                className="flex-1 mt-4 md:mt-0 rounded-none border-x-0 border-t-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:border-t md:border-l md:max-w-[300px]"
                placeholder="Surname ..."
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                required
              />
              <Button 
                type="submit" 
                className="w-full md:w-[80px] mt-4 md:mt-0 rounded-md md:rounded-l-none md:rounded-r-md"
                disabled={isInviting || isLoading}
              >
                {isInviting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Invite'}
              </Button>
            </div>
          </div>
        </form>
          
        {inviteErrorMessage && (
          <Notice tone="destructive" className="relative mb-8 flex items-center justify-between rounded px-4 py-3 text-base shadow-sm">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{inviteErrorMessage}</span>
            </div>
            <button
              onClick={() => setInviteErrorMessage('')}
              className="text-destructive"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </Notice>
        )}

        {successMessage && (
          <Notice tone="success" className="relative mb-8 flex items-center justify-between rounded px-4 py-3 text-base shadow-sm">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{successMessage}</span>
            </div>
            <button 
              onClick={() => setSuccessMessage('')}
              className="text-success"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </Notice>
        )}
      </div>

      <div className="pt-4">
        <h3 className="text-lg font-medium mb-4">Current Members</h3>
        
        {adminErrorMessage && (
          <Notice tone="destructive" className="relative mb-4 flex items-center justify-between rounded px-4 py-3 text-base">
            <span>{adminErrorMessage}</span>
            <button 
              onClick={() => setAdminErrorMessage('')}
              className="text-destructive"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </Notice>
        )}

        <HubAdminMemberActions setAdminErrorMessage={setAdminErrorMessage} />

        {renderMemberList(adminMembers, 'Administrators')}
        {renderMemberList(nonAdminMembers, 'Standard Users')}
      </div>
    </div>
  );
} 